import { Box, Typography } from "@mui/material";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { ChartDisplayType } from "glue/Chart/chart.constants";
import { ChartTypeSwitch } from "legacyNext/components/Charts/ChartTypeSwitch";
import { useAssetIntensity } from "legacyNext/hooks";
import { ReductionProposal } from "legacyNext/types/";

import { S } from "./reduction-analysis.styles";
import {
    getFormattedChartData,
    getReductionDetails,
} from "./reduction-analysis.utils";
import { ReductionAnalysisChart } from "./ReductionAnalysisChart";

type Props = {
    assetId: string;
    proposals: Array<ReductionProposal>;
    area: number | null;
};

export const ReductionAnalysisView = (props: Props) => {
    const { assetId, proposals, area } = props;
    const { t } = useTranslation();

    const [type, setType] = useState<ChartDisplayType>(
        ChartDisplayType.EMISSIONS
    );

    const enabledProposals = proposals.filter(
        (proposal) => proposal.enabled && proposal.startYear
    );

    const { data, pathways, loading } = useAssetIntensity(assetId, type);

    const chartData = useMemo(() => {
        if (!area) return [];

        return getFormattedChartData(area, type, enabledProposals, data);
    }, [area, data, enabledProposals, type]);

    const details = getReductionDetails(
        t,
        pathways,
        enabledProposals,
        chartData
    );

    return (
        <>
            <Typography variant="h2">
                {t("legacyNext.reductionAnalysis", "reduction analysis")}
            </Typography>
            <Typography>
                {t(
                    "legacyNext.reductionAnalysisSubtitle",
                    "overview of how the property performs relative to the market. calculate and track the property's sustainability progress and view its footprint as either emissions or energy intensity."
                )}
            </Typography>
            <ChartTypeSwitch current={type} onChange={setType} />
            <Box display="flex" gap={2} my={4}>
                {details.map((detail) => (
                    <S.Card key={detail.id} variant="outlined">
                        <Typography variant="h5" fontWeight={600}>
                            {detail.label.toUpperCase()}
                        </Typography>
                        <Typography fontSize={24} fontWeight={600}>
                            {detail.value ?? "-"}
                        </Typography>
                        <Typography>{detail.unit}</Typography>
                    </S.Card>
                ))}
            </Box>
            <ReductionAnalysisChart
                type={type}
                data={chartData}
                loading={loading}
                pathways={pathways}
                proposals={enabledProposals}
            />
        </>
    );
};
